import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, ImagePlus, Languages, Loader2, Mail, MailCheck, Newspaper, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../auth";
import { googleLogin, Logo } from "./Header";
import { NewsletterComposer } from "./NewsletterComposer";
import { formatNewsDate } from "./News";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-primary focus:bg-white/[0.08] focus:ring-2 focus:ring-primary/30";

const EMPTY = { title: "", excerpt: "", content: "" };
const EMPTY_TR = { en: { ...EMPTY } };
const TAB_LABELS = { it: "Italiano", en: "English" };

export default function NewsAdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [tr, setTr] = useState(EMPTY_TR);
  const [tab, setTab] = useState("it");
  const [translating, setTranslating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePath, setImagePath] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("news");
  const [subs, setSubs] = useState(null);
  const fileRef = useRef(null);

  const loadSubs = () =>
    axios
      .get(`${API}/admin/newsletter/subscribers`, { withCredentials: true })
      .then((res) => setSubs(res.data.items || []))
      .catch(() => setSubs([]));

  useEffect(() => {
    if (view === "subscribers" && subs === null && user?.is_admin) loadSubs();
  }, [view, user]);

  const load = () =>
    axios.get(`${API}/news`).then((res) => setItems(res.data.items || [])).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Rimuovere ${email} dagli iscritti alla newsletter?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success("Iscritto rimosso.");
      loadSubs();
    } catch {
      toast.error("Rimozione non riuscita.");
    }
  };

  const resetForm = () => {
    setForm(EMPTY);
    setTr(EMPTY_TR);
    setTab("it");
    setEditingId(null);
    setImagePath(null);
    setImageFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startEdit = (n) => {
    setEditingId(n.news_id);
    setForm({ title: n.title, excerpt: n.excerpt, content: n.content });
    setTr({
      en: { ...EMPTY, ...(n.translations?.en || {}) },
    });
    setTab("it");
    setImagePath(n.image_path || null);
    setImageFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const translate = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Compila prima titolo e testo in italiano.");
      return;
    }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/admin/news/translate`, form, { withCredentials: true });
      setTr({ en: res.data.en });
      setTab("en");
      toast.success("Traduzione pronta! Controlla l'inglese prima di pubblicare.");
    } catch {
      toast.error("Traduzione non riuscita. Riprova.");
    } finally {
      setTranslating(false);
    }
  };

  const removeNews = async (n) => {
    if (!window.confirm(`Eliminare la news "${n.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/news/${n.news_id}`, { withCredentials: true });
      toast.success("News eliminata.");
      if (editingId === n.news_id) resetForm();
      load();
    } catch {
      toast.error("Eliminazione non riuscita.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
      setTab("it");
      toast.error("Compila titolo, sottotitolo e testo in italiano.");
      return;
    }
    setSaving(true);
    try {
      let finalImagePath = imagePath;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await axios.post(`${API}/admin/news/upload`, fd, { withCredentials: true });
        finalImagePath = up.data.image_path;
      }
      const payload = { ...form, image_path: finalImagePath, translations: tr };
      if (editingId) {
        await axios.put(`${API}/admin/news/${editingId}`, payload, { withCredentials: true });
        toast.success("News aggiornata!");
      } else {
        await axios.post(`${API}/admin/news`, payload, { withCredentials: true });
        toast.success("News pubblicata!");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salvataggio non riuscito. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = imageFile
    ? URL.createObjectURL(imageFile)
    : imagePath
      ? `${API}/news-images/${imagePath}`
      : null;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#04122e]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Logo onClick={() => navigate("/")} />
          <button
            data-testid="admin-back-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft size={14} />
            Torna alla home
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pb-28 pt-32 md:px-8">
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !user ? (
          <div className="mx-auto max-w-md rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center" data-testid="admin-login-prompt">
            <h1 className="font-serif text-3xl text-white">Area riservata</h1>
            <p className="mt-3 text-sm text-white/60">
              Accedi con l'account Google autorizzato per gestire le news.
            </p>
            <button
              data-testid="admin-login-button"
              onClick={googleLogin}
              className="mt-8 rounded-full bg-primary px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all hover:bg-[#e0562a]"
            >
              Accedi con Google
            </button>
          </div>
        ) : !user.is_admin ? (
          <div className="mx-auto max-w-md rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center" data-testid="admin-denied">
            <h1 className="font-serif text-3xl text-white">Accesso negato</h1>
            <p className="mt-3 text-sm text-white/60">
              L'account <span className="text-white">{user.email}</span> non è autorizzato a gestire le news.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-10 flex items-center rounded-full border border-white/15 p-1 md:w-fit" data-testid="admin-view-tabs">
              <button
                data-testid="admin-view-news"
                onClick={() => setView("news")}
                className={`flex items-center gap-2 rounded-full px-5 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  view === "news" ? "bg-white text-[#04122e]" : "text-white/60 hover:text-white"
                }`}
              >
                <Newspaper size={14} />
                News
              </button>
              <button
                data-testid="admin-view-subscribers"
                onClick={() => setView("subscribers")}
                className={`flex items-center gap-2 rounded-full px-5 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  view === "subscribers" ? "bg-white text-[#04122e]" : "text-white/60 hover:text-white"
                }`}
              >
                <Mail size={14} />
                Iscritti newsletter
              </button>
            </div>

            {view === "subscribers" ? (
              <div data-testid="admin-subscribers-panel">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
                      Newsletter
                    </p>
                    <h1 className="mt-3 font-serif text-4xl tracking-tight text-white">
                      Iscritti ({subs?.length ?? "…"})
                    </h1>
                  </div>
                  <button
                    data-testid="admin-subscribers-copy"
                    onClick={() => {
                      navigator.clipboard.writeText((subs || []).map((s) => s.email).join(", "));
                      toast.success("Email copiate negli appunti!");
                    }}
                    className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors hover:border-white/50 hover:text-white"
                  >
                    <Copy size={13} />
                    Copia tutte le email
                  </button>
                </div>
                <div className="mt-8 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-subscribers-list">
                  {(subs || []).map((s, i) => (
                    <div
                      key={s.email}
                      className={`flex flex-wrap items-center gap-3 px-5 py-4 ${i % 2 ? "bg-white/[0.02]" : "bg-white/[0.05]"}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-white">{s.email}</span>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/40">
                        {formatNewsDate(s.created_at, "it")}
                      </span>
                      {s.welcome_email_id ? (
                        <span className="flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-300">
                          <MailCheck size={12} />
                          Benvenuto inviato
                        </span>
                      ) : (
                        <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/40">
                          —
                        </span>
                      )}
                      <button
                        data-testid={`admin-subscriber-delete-${s.email}`}
                        onClick={() => removeSubscriber(s.email)}
                        className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white/50 transition-colors hover:border-red-400 hover:text-red-400"
                        title="Rimuovi iscritto"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  {subs !== null && subs.length === 0 && (
                    <p className="px-5 py-8 text-sm text-white/40">Nessun iscritto per ora.</p>
                  )}
                </div>
                <NewsletterComposer subsCount={subs?.length ?? 0} />
              </div>
            ) : (
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
                Pannello amministrazione
              </p>
              <h1 className="mt-4 font-serif text-4xl tracking-tight text-white md:text-5xl">
                {editingId ? "Modifica news" : "Pubblica una news"}
              </h1>
              <form onSubmit={submit} className="mt-8 flex flex-col gap-4" data-testid="admin-news-form">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center rounded-full border border-white/15 p-1" data-testid="admin-lang-tabs">
                    {["it", "en"].map((l) => {
                      const filled = l === "it" ? !!form.title : !!tr[l]?.title;
                      return (
                        <button
                          key={l}
                          type="button"
                          data-testid={`admin-lang-tab-${l}`}
                          onClick={() => setTab(l)}
                          className={`relative rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-300 ${
                            tab === l ? "bg-white text-[#04122e]" : "text-white/60 hover:text-white"
                          }`}
                        >
                          {TAB_LABELS[l]}
                          {l !== "it" && filled && (
                            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    data-testid="admin-translate-button"
                    onClick={translate}
                    disabled={translating}
                    className="flex items-center gap-2 rounded-full border border-primary/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary transition-all hover:bg-primary/10 disabled:opacity-60"
                  >
                    {translating ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
                    {translating ? "Traduco…" : "Traduci con AI"}
                  </button>
                </div>
                {tab !== "it" && (
                  <p className="text-[12px] leading-relaxed text-white/45" data-testid="admin-tr-hint">
                    Versione {TAB_LABELS[tab]}: generata dall'AI, puoi correggerla. Se lasciata vuota, i visitatori vedranno l'italiano.
                  </p>
                )}
                {["it", "en"].map((l) => {
                  if (l !== tab) return null;
                  const cur = l === "it" ? form : tr[l];
                  const setCur = (patch) =>
                    l === "it" ? setForm({ ...form, ...patch }) : setTr({ ...tr, [l]: { ...tr[l], ...patch } });
                  return (
                    <div key={l} className="flex flex-col gap-4">
                      <input
                        data-testid="admin-news-title"
                        required={l === "it"}
                        value={cur.title}
                        onChange={(e) => setCur({ title: e.target.value })}
                        placeholder={l === "it" ? "Titolo" : `Titolo (${TAB_LABELS[l]})`}
                        className={inputCls}
                      />
                      <textarea
                        data-testid="admin-news-excerpt"
                        required={l === "it"}
                        rows={2}
                        maxLength={500}
                        value={cur.excerpt}
                        onChange={(e) => setCur({ excerpt: e.target.value })}
                        placeholder={l === "it" ? "Sottotitolo breve (appare nell'elenco in home)" : `Sottotitolo (${TAB_LABELS[l]})`}
                        className={`${inputCls} resize-none`}
                      />
                      <textarea
                        data-testid="admin-news-content"
                        required={l === "it"}
                        rows={8}
                        value={cur.content}
                        onChange={(e) => setCur({ content: e.target.value })}
                        placeholder={l === "it" ? "Testo completo della news (vai a capo per creare paragrafi)" : `Testo completo (${TAB_LABELS[l]})`}
                        className={`${inputCls} resize-y`}
                      />
                    </div>
                  );
                })}
                <div className="rounded-2xl border border-dashed border-white/20 p-4">
                  {previewUrl ? (
                    <div className="relative">
                      <img src={previewUrl} alt="Anteprima" className="max-h-56 w-full rounded-xl object-cover" data-testid="admin-image-preview" />
                      <button
                        type="button"
                        data-testid="admin-image-remove"
                        onClick={() => {
                          setImageFile(null);
                          setImagePath(null);
                          if (fileRef.current) fileRef.current.value = "";
                        }}
                        className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid="admin-image-pick"
                      onClick={() => fileRef.current?.click()}
                      className="flex w-full flex-col items-center gap-2 py-6 text-white/50 transition-colors hover:text-white"
                    >
                      <ImagePlus size={26} />
                      <span className="text-sm">Aggiungi una foto (JPG/PNG/WEBP, max 8 MB)</span>
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    data-testid="admin-image-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    data-testid="admin-news-submit"
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all hover:bg-[#e0562a] disabled:opacity-60"
                  >
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                    {saving ? "Salvataggio…" : editingId ? "Salva modifiche" : "Pubblica"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      data-testid="admin-news-cancel-edit"
                      onClick={resetForm}
                      className="rounded-full border border-white/20 px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-white/70 transition-colors hover:text-white"
                    >
                      Annulla
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white">News pubblicate ({items.length})</h2>
              <div className="mt-6 flex flex-col gap-4" data-testid="admin-news-list">
                {items.map((n) => (
                  <div
                    key={n.news_id}
                    data-testid={`admin-news-row-${n.news_id}`}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                  >
                    {n.image_path ? (
                      <img
                        src={`${API}/news-images/${n.image_path}`}
                        alt=""
                        className="h-14 w-20 shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="grid h-14 w-20 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-white/30">
                        <ImagePlus size={18} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-serif text-lg text-white">{n.title}</p>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-white/40">
                        {formatNewsDate(n.created_at, "it")}
                      </p>
                    </div>
                    <button
                      data-testid={`admin-news-edit-${n.news_id}`}
                      onClick={() => startEdit(n)}
                      className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-primary hover:text-primary"
                      title="Modifica"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`admin-news-delete-${n.news_id}`}
                      onClick={() => removeNews(n)}
                      className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-red-400 hover:text-red-400"
                      title="Elimina"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {items.length === 0 && (
                  <p className="text-sm text-white/40">Nessuna news ancora. Pubblica la prima!</p>
                )}
              </div>
            </div>
          </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
